import React, {useEffect, useState} from 'react'
import {getDemandesWithoutPagination} from './requests'
import {ModeleClientDemande} from './Models'

interface SearchDemandeProps {
  placeholder?: string
  setDemande: (demande: ModeleClientDemande | undefined | null) => void
  initialDemande?: ModeleClientDemande | undefined | null
  disabled?: boolean
}

const SearchDemande: React.FC<SearchDemandeProps> = ({placeholder, setDemande, initialDemande, disabled}) => {
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [filteredDemandes, setFilteredDemandes] = useState<ModeleClientDemande[]>([])
  const [loading, setLoading] = useState(false)
  const [showResults, setShowResults] = useState(false)

  useEffect(() => {
    const isInteger = (str: string) => /^\d+$/.test(str)
    if (searchTerm.length === 0) {
      setFilteredDemandes([])
      setDemande(null)
      return
    }

    if (!isInteger(searchTerm) && searchTerm.length < 3) {
      setFilteredDemandes([])
      return
    }

    const timer = setTimeout(() => {
      setLoading(true)
      getDemandesWithoutPagination(searchTerm)
        .then((res) => {
          setFilteredDemandes(res.data[0] ?? [])
        })
        .finally(() => setLoading(false))
    }, 400)

    return () => clearTimeout(timer)
  }, [searchTerm])

  useEffect(() => {
    if (initialDemande) {
      setSearchTerm(buildSearchTermFromDemande(initialDemande))
      setDemande(initialDemande)
    }
  }, [initialDemande])

  const handleSelect = (demande: ModeleClientDemande) => {
    setDemande(demande)
    setShowResults(false)
    setSearchTerm(buildSearchTermFromDemande(demande))
  }

  const buildSearchTermFromDemande = (demande: ModeleClientDemande): string => {
    const immatriculations = demande.immatriculation
      ? demande.immatriculation.split(',').slice(0, 2).join(' - ')
      : ''

    const clientNom = demande.client
      ? `${demande.client.nom ?? ''} ${demande.client.prenom ?? ''}`.trim()
      : ''

    const utilisateurNom = demande.utilisateurAssigne
      ? `${demande.utilisateurAssigne.nom ?? ''} ${demande.utilisateurAssigne.prenom ?? ''}`.trim()
      : ''

    return [demande.id, clientNom, utilisateurNom, immatriculations].filter(Boolean).join(' ')
  }

  return (
    <div className='position-relative'>
      <input
        type='text'
        className='form-control'
        placeholder={placeholder ?? 'Rechercher...'}
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value)
          setShowResults(true)
        }}
        autoComplete='off'
        disabled={disabled}
      />

      {showResults && searchTerm && (
        <div
          className='border bg-white position-absolute w-100 shadow-sm mt-1'
          style={{zIndex: 1000, maxHeight: 250, overflowY: 'auto'}}
        >
          {loading && <div className='px-3 py-2 text-muted'>Chargement...</div>}

          {!loading && filteredDemandes.length === 0 && (
            <div className='px-3 py-2 text-danger'>Aucun résultat trouvé</div>
          )}

          {!loading &&
            filteredDemandes.map((demande) => (
              <div
                key={demande.id}
                className='px-3 py-2 search-result-item cursor-pointer hover-bg-light'
                style={{cursor: 'pointer'}}
                onClick={() => handleSelect(demande[0])}
              >
                <strong>{demande[0].id}</strong>
                {demande[0].client && (
                  <span className='text-muted'>
                    {' '}
                    {demande[0].client.nom ?? ''} {demande[0].client.prenom ?? ''}
                  </span>
                )}
                {demande[0].utilisateurAssigne && (
                  <span className='text-muted'>
                    {' '}
                    {demande[0].utilisateurAssigne.nom ?? ''}{' '}
                    {demande[0].utilisateurAssigne.prenom ?? ''}
                  </span>
                )}
                {demande['immatriculation'] && (
                  <span className='text-muted'>
                    {' '}
                    {(() => {
                      const immats = demande['immatriculation']
                        .split(',')
                        .map((i: string) => i.trim())
                      const displayed = immats.slice(0, 2).join(' - ')
                      return immats.length > 2 ? `${displayed}...` : displayed
                    })()}
                  </span>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  )
}

export default SearchDemande
